import { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import FormInput from "../components/custom/FormInput";
import VerticalForm from "../components/custom/components/VerticalForm";
import AuthLayout from "../components/custom/pages/AuthLayout";
import { ToastTypes, useToast } from "../components/toast/ToastProvider";
import { AppDispatch } from "../redux/store";
import {
  requestOtpAction,
  verifyOtpAction,
  resetPasswordAction,
} from "../redux/actions/forgotPasswordAction";
import { useNavigate } from "react-router-dom";

const ForgotPasswordPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  const [employeeId, setEmployeeId] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const handleChange = (field: string, value: string) => {
    if (field === "employeeId") setEmployeeId(value);
    if (field === "otp") setOtp(value);
    if (field === "password") setPassword(value);
    if (field === "confirmPassword") setConfirmPassword(value);
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateStep = () => {
    const newErrors: { [key: string]: string } = {};
    if (step === 1 && !employeeId.trim())
      newErrors.employeeId = "Employee ID is required";
    if (step === 2 && !otp.trim()) newErrors.otp = "OTP is required";
    if (step === 3) {
      if (!password.trim()) newErrors.password = "Password is required";
      if (password !== confirmPassword)
        newErrors.confirmPassword = "Passwords do not match";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    try {
      if (step === 1) {
        await dispatch(requestOtpAction({ employeeId }));
        toast("OTP sent successfully");
        setStep(2);
      } else if (step === 2) {
        await dispatch(verifyOtpAction(employeeId, { otp }));
        toast("OTP verified");
        setStep(3);
      } else if (step === 3) {
        await dispatch(resetPasswordAction(employeeId, { password }));
        toast("Password reset successful");
        navigate("/login");
      }
    } catch (err: any) {
      toast(err, ToastTypes.ERROR);
    } finally {
      if (mounted) setLoading(false);
    }
  };

  return (
    <AuthLayout helpText="Forgot Password">
      <VerticalForm onSubmit={handleSubmit}>
        {step === 1 && (
          <FormInput
            label="Employee ID"
            type="text"
            name="employeeId"
            value={employeeId}
            placeholder="Enter your Employee ID"
            onChange={(e) => handleChange("employeeId", e.target.value)}
            errorText={errors.employeeId}
            containerClass="mb-3"
          />
        )}

        {step === 2 && (
          <FormInput
            label="OTP"
            type="text"
            name="otp"
            value={otp}
            placeholder="Enter OTP"
            onChange={(e) => handleChange("otp", e.target.value)}
            errorText={errors.otp}
            containerClass="mb-3"
          />
        )}

        {step === 3 && (
          <>
            <FormInput
              label="New Password"
              type="password"
              name="password"
              value={password}
              placeholder="Enter new password"
              onChange={(e) => handleChange("password", e.target.value)}
              errorText={errors.password}
              containerClass="mb-3"
            />
            <FormInput
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              value={confirmPassword}
              placeholder="Confirm new password"
              onChange={(e) => handleChange("confirmPassword", e.target.value)}
              errorText={errors.confirmPassword}
              containerClass="mb-3"
            />
          </>
        )}

        <div className="d-grid text-right">
          <Button
            type="submit"
            className="btn-sm p-2"
            style={{ backgroundColor: "#10296C", color: "#fff" }}
            disabled={loading}
          >
            {loading
              ? step === 1
                ? "Sending OTP..."
                : step === 2
                  ? "Verifying OTP..."
                  : "Resetting..."
              : step === 1
                ? "Send OTP"
                : step === 2
                  ? "Verify OTP"
                  : "Reset Password"}
          </Button>
        </div>
      </VerticalForm>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
